"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  CartState,
  CartItem,
  loadCart,
  saveCart,
  addItemToCart,
  removeItemFromCart,
  updateItemQuantity,
  cartTotalEur,
  cartCount,
} from "@/lib/cart";

interface Toast {
  id: number;
  productName: string;
}

interface CartContextValue {
  cart: CartState;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (sku: string) => void;
  updateQuantity: (sku: string, quantity: number) => void;
  /** Subtotal in EUR. Display layer converts via useCurrency() + formatPrice(). */
  totalEur: number;
  count: number;
  toasts: Toast[];
  dismissToast: (id: number) => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<CartState>({ items: [] });
  const [isOpen, setIsOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  // Load from localStorage on mount
  useEffect(() => {
    setCart(loadCart());
  }, []);

  // Persist to localStorage whenever cart changes
  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const addItem = useCallback((item: Omit<CartItem, "quantity">) => {
    setCart((prev) => addItemToCart(prev, item));
    // Toast
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, productName: item.shortName || item.name }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  const removeItem = useCallback((sku: string) => {
    setCart((prev) => removeItemFromCart(prev, sku));
  }, []);

  const updateQuantity = useCallback((sku: string, quantity: number) => {
    setCart((prev) => updateItemQuantity(prev, sku, quantity));
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const totalEur = cartTotalEur(cart);
  const count = cartCount(cart);

  return (
    <CartContext.Provider
      value={{
        cart,
        isOpen,
        openCart,
        closeCart,
        addItem,
        removeItem,
        updateQuantity,
        totalEur,
        count,
        toasts,
        dismissToast,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
